import { loadStripe, type Stripe } from '@stripe/stripe-js';

/**
 * AuctiSync uses Stripe Connect (Standard). Every charge runs on a
 * tenant's Stripe account, identified by `stripe_account_id` (acct_xxx).
 * Stripe.js must therefore be initialized with `{ stripeAccount }` per
 * call — Elements is bound to a specific account at load time.
 *
 * This helper caches one Stripe instance per (publishable key, account)
 * pair so successive payments on the same tenant share a warmed SDK.
 */

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

type CacheKey = string;
const cache = new Map<CacheKey, Promise<Stripe | null>>();

export function stripeConfigured(): boolean {
  return typeof PUBLISHABLE_KEY === 'string' && PUBLISHABLE_KEY.startsWith('pk_');
}

export function missingStripeKeyMessage(): string {
  return 'Stripe publishable key is not configured. Set VITE_STRIPE_PUBLISHABLE_KEY in your environment.';
}

export function loadStripeForAccount(stripeAccountId: string): Promise<Stripe | null> {
  if (!PUBLISHABLE_KEY) {
    return Promise.reject(new Error(missingStripeKeyMessage()));
  }
  const key: CacheKey = `${PUBLISHABLE_KEY}::${stripeAccountId}`;
  let entry = cache.get(key);
  if (!entry) {
    entry = loadStripe(PUBLISHABLE_KEY, { stripeAccount: stripeAccountId });
    cache.set(key, entry);
  }
  return entry;
}
